/**
 * AI Review tab — on-demand (re-)trigger of the loan AI agents, the findings
 * queue split by category, and the Loan-Opening field-change diff.
 *
 * Admin-only (the endpoints are gated behind require_admin on the backend; the
 * nav entry is added only for admins in LoanDrilldownScreen).
 *
 * Layout: the tab is FULL-BLEED, split like the `/admin/*` screens — a LEFT
 * SECTION RAIL flush against the screen edge and the section body filling the
 * rest (capped at BODY_MAX_W). The rail carries the PAGE HEADER (title, the
 * admin chip, the one-line description) above the section list, so the body is
 * nothing but content; it collapses to a 60px icon rail, remembered per partner.
 * That's why there's no `LoanTabShell`/`LoanTabCanvas` here — a centred
 * max-width container can't put the rail on the screen's edge, and the header
 * doesn't live in the body column any more.
 *
 * Sections (grouped, live count badges, teal active tint + edge marker — the
 * same anatomy as `AdminLayout`'s sidebar):
 *   • AI Agents            — the agent run table (DEFAULT).
 *   • Opening Data Assistant (PRE-CLOSE only) — propose→accept field writes.
 *   • Suggested Hard Exceptions — findings from the hard-exception checker
 *                            (project exception requests) + the summary block.
 *   • Suggested Conditions — every other finding (project conditions).
 *   • Modified Loan Data   — the Loan-Opening field changes (hidden until >0).
 * (There is no "soft exception" split — the findings API carries no hard/soft or
 * condition/exception flag beyond `sourceAgent === "hard_exception_checker"`.)
 *
 * All section bodies stay mounted (hidden, not unmounted) so their counts stay
 * current regardless of which one shows; each reports its count via `onCount`.
 * The two findings sections are one AiFindingsPanel each, `scope`-filtered.
 */
import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import AutoAwesomeOutlined from "@mui/icons-material/AutoAwesomeOutlined";
import AutoFixHighOutlined from "@mui/icons-material/AutoFixHighOutlined";
import EditNoteOutlined from "@mui/icons-material/EditNoteOutlined";
import KeyboardDoubleArrowLeftRounded from "@mui/icons-material/KeyboardDoubleArrowLeftRounded";
import PsychologyOutlined from "@mui/icons-material/PsychologyOutlined";
import ReportProblemOutlined from "@mui/icons-material/ReportProblemOutlined";
import RuleOutlined from "@mui/icons-material/RuleOutlined";

import Flx from "../../../../components/layout/Flx";
import Txt from "../../../../components/typography/Txt";
import { useIsAcquisitions } from "../../../../pipelines/PipelineContext";
import { partnerStorage } from "../../../../utils/storage/partnerStorage";
import { A } from "../../../admin/shared/adminTokens";
import LoanDrilldownTabPanel from "../LoanDrilldownTabPanel";
import AiReviewsPanel from "../../components/AiReviewsPanel";
import AiHardExceptionPanel from "../../components/AiHardExceptionPanel";
import AiFindingsPanel from "../../components/AiFindingsPanel";
import VisibilityOutlined from "@mui/icons-material/VisibilityOutlined";
import {
  AI_SUGGESTION_SCOPES,
  useAiSuggestionIgnored,
} from "../../../../utils/storage/aiSuggestionVisibility";
import OpeningFieldWritesPanel from "../../components/OpeningFieldWritesPanel";
import OpeningDataAssistantPanel from "../../components/OpeningDataAssistantPanel";

// The Opening Data Assistant is PRE-CLOSE only (it maps origination extracts into
// loan fields). Acquisitions loans skip it.
const SECTIONS = [
  {
    key: "agents",
    label: "AI Agents",
    group: "Run",
    icon: AutoAwesomeOutlined,
  },
  {
    key: "assistant",
    label: "Opening Data Assistant",
    group: "Run",
    icon: AutoFixHighOutlined,
  },
  {
    key: "hard",
    label: "Suggested Hard Exceptions",
    group: "Results",
    icon: ReportProblemOutlined,
  },
  {
    key: "conditions",
    label: "Suggested Conditions",
    group: "Results",
    icon: RuleOutlined,
  },
  {
    key: "updates",
    label: "Modified Loan Data",
    group: "Results",
    icon: EditNoteOutlined,
  },
];
const GROUP_ORDER = ["Run", "Results"];

// Rail widths — the expanded panel and the collapsed icon rail, matching the
// admin sidebar. Under NARROW the rail stacks above the body instead of taking a
// column of its own.
const RAIL_W = 300;
const RAIL_COLLAPSED_W = 60;
const NARROW = "@media (max-width: 900px)";
const COLLAPSE_KEY = "aiReviewRailCollapsed";
// The body is full-bleed apart from this gutter, which keeps the panel cards off
// the rail border and the window edge, and the width cap (the admin section's
// number — one cap for every wide surface).
const BODY_GUTTER = 1.75;
const BODY_MAX_W = 1800;

// Count badge next to a section label. Red draws the eye to open hard
// exceptions, amber to suggested conditions, slate is the neutral count.
const TONES = {
  red: { bg: "#fee2e2", color: "#b91c1c", dot: "#dc2626" },
  amber: { bg: "#fde6b8", color: "#92610a", dot: "#d97706" },
  slate: { bg: "#e2e8f0", color: "#475569", dot: "#94a3b8" },
  slateActive: { bg: "#dbe1ea", color: "#334155", dot: "#94a3b8" },
};

const toneStyles = (tone, active) =>
  tone === "slate" && active ? TONES.slateActive : TONES[tone] || TONES.slate;

const TabBadge = ({ children, tone = "slate", active }) => {
  const t = toneStyles(tone, active);
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        flexShrink: 0,
        ml: "auto",
        fontSize: 11,
        fontWeight: 700,
        lineHeight: 1.6,
        px: 0.75,
        borderRadius: "999px",
        bgcolor: t.bg,
        color: t.color,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </Box>
  );
};

/** One rail row — the AdminLayout `SidebarItem` anatomy. */
const RailItem = ({ section, active, badge, collapsed, onSelect }) => {
  const Icon = section.icon;
  const row = (
    <Box
      component="button"
      type="button"
      aria-current={active ? "page" : undefined}
      onClick={() => onSelect(section.key)}
      sx={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        gap: collapsed ? 0 : 1.25,
        justifyContent: collapsed ? "center" : "flex-start",
        width: "100%",
        mb: "1px",
        px: collapsed ? 0 : 1.25,
        py: collapsed ? 1.125 : 1,
        border: "none",
        borderRadius: "8px",
        cursor: "pointer",
        font: "inherit",
        fontSize: 13,
        fontWeight: active ? 600 : 500,
        textAlign: "left",
        lineHeight: 1.35,
        color: active ? A.tealDeep : A.ink2,
        bgcolor: active ? A.tealSoft : "transparent",
        transition: "background .13s, color .13s",
        "&:hover": {
          bgcolor: active ? A.tealSoft : A.bg,
          color: active ? A.tealDeep : A.ink,
        },
        "& svg": {
          fontSize: 18,
          flexShrink: 0,
          color: active ? A.tealDark : A.faint,
        },
        "&:hover svg": { color: active ? A.tealDark : A.ink2 },
        // The active marker rides the panel edge, outside the item's padding.
        ...(active && {
          "&::before": {
            content: '""',
            position: "absolute",
            right: -10,
            top: 6,
            bottom: 6,
            width: 3,
            borderRadius: "3px 0 0 3px",
            bgcolor: A.teal,
          },
        }),
      }}
    >
      {Icon ? <Icon /> : null}
      {collapsed ? null : <span>{section.label}</span>}
      {/* Collapsed there's no room for the count, so it becomes a dot on the
          icon — the queue still reads as "something is waiting here". */}
      {collapsed
        ? badge && (
            <Box
              sx={{
                position: "absolute",
                top: 7,
                right: 12,
                width: 7,
                height: 7,
                borderRadius: "999px",
                bgcolor: toneStyles(badge.tone, active).dot,
              }}
            />
          )
        : badge && (
            <TabBadge tone={badge.tone} active={active}>
              {badge.label}
            </TabBadge>
          )}
    </Box>
  );

  // Collapsed, the icon is the only affordance — the label becomes the tooltip.
  return collapsed ? (
    <Tooltip title={section.label} placement="right" disableInteractive>
      {row}
    </Tooltip>
  ) : (
    row
  );
};

const AiReviewTab = ({ value, tab }) => {
  const isAcquisitions = useIsAcquisitions();
  // `?section=<key>` (from a notification link) picks the opening section, and
  // re-selects it when a new link arrives while the tab is already open.
  const [searchParams] = useSearchParams();
  const sectionParam = searchParams.get("section");
  const [active, setActive] = useState(sectionParam || "agents");
  useEffect(() => {
    if (sectionParam) setActive(sectionParam);
  }, [sectionParam]);
  const [collapsed, setCollapsed] = useState(
    () => partnerStorage.getItem(COLLAPSE_KEY) === "1",
  );
  const [agents, setAgents] = useState(null); // { ran, total }
  const [hardCount, setHardCount] = useState(0);
  // These two only matter when the matching tab's panel has been dismissed —
  // that's the only case where a "Show in … tab" button appears here.
  const { ignored: exceptionsIgnored, show: showInExceptionsTab } =
    useAiSuggestionIgnored(AI_SUGGESTION_SCOPES.EXCEPTIONS);
  const { ignored: conditionsIgnored, show: showInConditionsTab } =
    useAiSuggestionIgnored(AI_SUGGESTION_SCOPES.CONDITIONS);
  const [condCount, setCondCount] = useState(0);
  const [updatesCount, setUpdatesCount] = useState(0);
  const [assistantCount, setAssistantCount] = useState(0);

  const toggleCollapsed = useCallback(() => {
    setCollapsed((prev) => {
      partnerStorage.setItem(COLLAPSE_KEY, prev ? "0" : "1");
      return !prev;
    });
  }, []);

  // The Assistant is pre-close only; Modified Loan Data hides until the loan has
  // field changes (its body stays mounted below so it can reappear). Fall back
  // to the first visible section if the active one is hidden.
  const visibleSections = SECTIONS.filter((s) => {
    if (s.key === "assistant") return !isAcquisitions;
    if (s.key === "updates") return updatesCount > 0;
    return true;
  });
  const effectiveActive = visibleSections.some((s) => s.key === active)
    ? active
    : visibleSections[0]?.key || "agents";

  const groups = GROUP_ORDER.map((label) => ({
    label,
    items: visibleSections.filter((s) => s.group === label),
  })).filter((g) => g.items.length);

  const badgeFor = (key) => {
    if (key === "agents") {
      return agents
        ? { label: `${agents.ran}/${agents.total} ran`, tone: "slate" }
        : null;
    }
    if (key === "assistant") {
      return assistantCount > 0
        ? { label: assistantCount, tone: "slate" }
        : null;
    }
    if (key === "hard") {
      return hardCount > 0 ? { label: hardCount, tone: "red" } : null;
    }
    if (key === "conditions") {
      return condCount > 0 ? { label: condCount, tone: "amber" } : null;
    }
    return updatesCount > 0 ? { label: updatesCount, tone: "slate" } : null;
  };

  return (
    <LoanDrilldownTabPanel value={value} tab={tab}>
      <Flx
        sx={{
          flex: 1,
          minHeight: 0,
          alignItems: "stretch",
          [NARROW]: { flexDirection: "column" },
        }}
      >
        {/* Section rail — flush left, full height, the admin sidebar's anatomy. */}
        <Box
          component="nav"
          aria-label="AI review sections"
          sx={{
            width: collapsed ? RAIL_COLLAPSED_W : RAIL_W,
            flexShrink: 0,
            bgcolor: A.surface,
            borderRight: `1px solid ${A.line}`,
            transition: "width .22s cubic-bezier(.4,0,.2,1)",
            [NARROW]: {
              width: "100%",
              borderRight: "none",
              borderBottom: `1px solid ${A.line}`,
            },
          }}
        >
          {/* Pins under the drilldown's tab strip (itself sticky at the top of
              #overflow-root) so the rail stays reachable down a long queue. */}
          <Box
            sx={{
              position: "sticky",
              top: 0,
              pt: 1.5,
              pb: 1.25,
              [NARROW]: { position: "static", pt: 0 },
            }}
          >
            {/* Page header — title / chip / description live HERE, not over the
                body, which is what keeps the content column all content. */}
            <Box
              sx={{
                // Match the horizontal inset of the "Run" / "Results" group
                // labels below (container px 1.25 + label px 1.25 = 2.5) so the
                // header content lines up with them.
                px: collapsed ? 0.75 : 2.5,
                // No top inset — the title starts flush under the tab strip. The
                // 4px is the icon tile's optical breathing room, nothing more.
                pt: 0.5,
                pb: collapsed ? 1 : 1.25,
                borderBottom: `1px solid ${A.line}`,
              }}
            >
              <Flx
                ac
                g={1}
                sx={{ justifyContent: collapsed ? "center" : "flex-start" }}
              >
                {collapsed ? null : (
                  <>
                    <Flx
                      center
                      sx={{
                        width: 26,
                        height: 26,
                        borderRadius: "7px",
                        flexShrink: 0,
                        bgcolor: A.tealSoft,
                        color: A.tealDeep,
                      }}
                    >
                      <PsychologyOutlined sx={{ fontSize: 16 }} />
                    </Flx>
                    <Txt sx={{ fontSize: 13, fontWeight: 700, color: A.ink }}>
                      AI review
                    </Txt>
                    <Tooltip
                      arrow
                      placement="bottom"
                      title="You can see this tab because your account has admin access. Teammates without it won't."
                    >
                      <Chip
                        label="Admin only"
                        size="small"
                        sx={{
                          height: 18,
                          fontSize: 10,
                          fontWeight: 600,
                          background: "#f3e8ff",
                          color: "#5b21b6",
                          cursor: "help",
                          "& .MuiChip-label": { px: 0.75 },
                        }}
                      />
                    </Tooltip>
                  </>
                )}
                <Tooltip
                  title={collapsed ? "Expand" : "Collapse"}
                  placement="right"
                >
                  <IconButton
                    size="small"
                    onClick={toggleCollapsed}
                    aria-label={
                      collapsed ? "Expand sidebar" : "Collapse sidebar"
                    }
                    sx={{
                      ml: collapsed ? 0 : "auto",
                      color: A.faint,
                      borderRadius: "7px",
                      transition: "transform .22s",
                      transform: collapsed ? "rotate(180deg)" : "none",
                      "&:hover": { bgcolor: A.bg, color: A.ink },
                    }}
                  >
                    <KeyboardDoubleArrowLeftRounded sx={{ fontSize: 17 }} />
                  </IconButton>
                </Tooltip>
              </Flx>
              {collapsed ? null : (
                <Txt
                  sx={{
                    display: "block",
                    mt: 0.75,
                    fontSize: 11.5,
                    lineHeight: 1.45,
                    color: A.muted,
                  }}
                >
                  Use AI to analyze the loan's data, then review and manage the
                  findings it surfaces.
                </Txt>
              )}
            </Box>

            <Box sx={{ px: 1.25, pt: 1.25 }}>
              {groups.map((group, gi) => (
                <Box
                  key={group.label}
                  sx={{
                    mt: gi === 0 ? 0 : 2.75,
                    // Collapsed there are no group headings, so a hairline does
                    // the grouping instead of a label.
                    ...(collapsed && gi > 0
                      ? { mt: 0.75, pt: 0.75, borderTop: `1px solid ${A.line}` }
                      : null),
                  }}
                >
                  {collapsed ? null : (
                    <Txt
                      sx={{
                        display: "block",
                        fontSize: 10,
                        fontWeight: 700,
                        letterSpacing: ".1em",
                        textTransform: "uppercase",
                        color: A.faint,
                        px: 1.25,
                        pb: 0.5,
                      }}
                    >
                      {group.label}
                    </Txt>
                  )}
                  {group.items.map((s) => (
                    <RailItem
                      key={s.key}
                      section={s}
                      active={effectiveActive === s.key}
                      badge={badgeFor(s.key)}
                      collapsed={collapsed}
                      onSelect={setActive}
                    />
                  ))}
                </Box>
              ))}
            </Box>
          </Box>
        </Box>

        {/* Body column — all content, capped and centred at BODY_MAX_W. */}
        <Box sx={{ flex: 1, minWidth: 0, p: BODY_GUTTER }}>
          <Box sx={{ maxWidth: BODY_MAX_W, mx: "auto" }}>
            {/* Bodies — all mounted so counts stay live; only the active shows. */}
            <Box
              sx={{ display: effectiveActive === "agents" ? "block" : "none" }}
            >
              <AiReviewsPanel onCount={setAgents} />
            </Box>

            {!isAcquisitions && (
              <Box
                sx={{
                  display: effectiveActive === "assistant" ? "block" : "none",
                }}
              >
                <OpeningDataAssistantPanel onCount={setAssistantCount} />
              </Box>
            )}

            <Box
              sx={{ display: effectiveActive === "hard" ? "block" : "none" }}
            >
              <Flx column sx={{ gap: 2 }}>
                <AiHardExceptionPanel />
                <AiFindingsPanel
                  scope="hard"
                  onCount={setHardCount}
                  title="Suggested Hard Exceptions"
                  subtitle="Facility & product hard exceptions the checker staged. Promote to file an exception request, or reject / waive."
                  // Only offered once the panel has been dismissed from the
                  // Exceptions tab — this is the way back.
                  onIgnore={exceptionsIgnored ? showInExceptionsTab : null}
                  ignoreLabel="Show In Exceptions Tab"
                  ignoreIcon={
                    <VisibilityOutlined sx={{ fontSize: "16px !important" }} />
                  }
                  ignoreTooltip="Bring the AI suggested exceptions panel back to the Exceptions tab."
                />
              </Flx>
            </Box>

            <Box
              sx={{
                display: effectiveActive === "conditions" ? "block" : "none",
              }}
            >
              <AiFindingsPanel
                scope="conditions"
                onCount={setCondCount}
                title="Suggested Conditions"
                subtitle="Candidate conditions and missing-document findings the agents staged. Promote to a condition, or reject / waive."
                onIgnore={conditionsIgnored ? showInConditionsTab : null}
                ignoreLabel="Show In Conditions Tab"
                ignoreIcon={
                  <VisibilityOutlined sx={{ fontSize: "16px !important" }} />
                }
                ignoreTooltip="Bring the AI suggested conditions panel back to the Conditions tab."
              />
            </Box>

            <Box
              sx={{ display: effectiveActive === "updates" ? "block" : "none" }}
            >
              <OpeningFieldWritesPanel
                onCount={setUpdatesCount}
                defaultExpanded
              />
            </Box>
          </Box>
        </Box>
      </Flx>
    </LoanDrilldownTabPanel>
  );
};

export default AiReviewTab;
